import { useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useCreateQuoteFromQuoteMutation } from "./CreateQuoteFromQuoteModal.generated.js";

interface Props {
  projectId: string;
  sourceQuoteId: string;
  pathToDocList: string;
  handleClose: () => void;
}

export const CreateQuoteFromQuoteModal = ({
  projectId,
  sourceQuoteId,
  pathToDocList,
  handleClose,
}: Props) => {
  const { t } = useTranslate(["Quote", "QuoteCreate", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const client = useApolloClient();
  const [createQuoteFromQuote] = useCreateQuoteFromQuoteMutation({ client });

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);

      // `adoptOwnMaterialMargins` and `adoptOwnWorkRates` are deliberately not
      // sent: the input defaults both to `true`, and the product decision of
      // 2026-09-15 is that the craftsman's own settings always apply. The
      // fields stay on the mutation so offering the choice later is a change
      // here alone.
      const result = await createQuoteFromQuote({
        variables: { input: { sourceQuoteId, projectId } },
      });

      const newQuoteId = result.data?.createQuoteFromQuote.quote.id;

      if (!newQuoteId) {
        throw new Error(
          t("Something went wrong. Please try again.", { ns: "Global" })
        );
      }

      enqueueSnackbar(t("Quote created", { ns: "QuoteCreate" }), {
        variant: "success",
      });

      handleClose();

      navigate(`${pathToDocList}/${newQuoteId}`);
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          t("Something went wrong. Please try again.", { ns: "Global" }),
          { variant: "error" }
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      id={"create-quote-from-quote-modal"}
      title={t("Create quote from quote", { ns: "Quote" })}
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text", disabled: isSubmitting },
        },
        {
          label: t("Create", { ns: "Global" }),
          handleClick: handleConfirm,
          buttonProps: { disabled: isSubmitting, loading: isSubmitting },
        },
      ]}
    >
      <Typography variant="body2" color="text.secondary">
        {t(
          "The materials and quantities are taken over. The new quote is priced with your own material margins and hourly rates.",
          { ns: "Quote" }
        )}
      </Typography>
    </Modal>
  );
};
